<script lang="ts">
  import { preventDefault } from "svelte/legacy"
  import { onMount } from "svelte"
  import userStore, { userStoreActions } from "$lib/stores/userStore"
  import BackgroundGradient from "$lib/components/Background/BackgroundGradient.svelte"
  import { Button } from "$lib/components/ui/button"
  import HeaderSection from "./HeaderSection.svelte"
  import ActionSection from "./ActionSection.svelte"
  import StatsSection from "./StatsSection.svelte"
  import ImageSection from "./ImageSection.svelte"
  import { getParentUrl } from "./utils"
  import { statsStoreActions } from "$lib/stores/statsStore"
  import {
    getTimeLeft,
    isCompetitionEnded,
  } from "$lib/components/Widget2/utils"
  import { generateTerms } from "$lib/components/Widget/terms"
  import type { Competition } from "$lib/types"
  import { t, setDefaultLanguage, getTextDirection } from "$lib/utils/i18n"

  interface Props {
    competition: Competition
    embed?: boolean
    isCountryAllowed?: boolean
    showCreateButton?: boolean
    layout?: "portrait" | "landscape"
  }

  let {
    competition,
    embed = false,
    isCountryAllowed = true,
    showCreateButton = $bindable(
      competition?.widgetSettings?.showCreateButton || true,
    ),
    layout = $bindable(competition?.widgetSettings?.layout || "portrait"),
  }: Props = $props()

  // Set the default language based on competition settings
  setDefaultLanguage(competition?.language || "en")

  // Get text direction for RTL support
  const textDirection = $derived(getTextDirection(competition?.language))

  let isLoading = $state(true)
  let showTerms = $state(false)
  let showReferrals = $state(false)

  let affiliateUrl = $state(
    "https://www.promoamp.com/sign-up?utm_source=widget",
  )
  if (competition?.widgetSettings?.affiliateLink) {
    affiliateUrl = competition.widgetSettings?.affiliateLink
  }

  let backgroundColor = $derived(
    Array.isArray(competition?.widgetCustomizations?.stops) &&
      competition?.widgetCustomizations?.stops.length > 0
      ? competition?.widgetCustomizations?.stops.length === 1
        ? competition?.widgetCustomizations?.stops[0].color
        : `linear-gradient(to right, ${competition?.widgetCustomizations?.stops.map((stop) => `${stop.color} ${stop.stopValue}%`).join(", ")})`
      : "#ffffff",
  )

  function openTermsDialog() {
    if (window.innerWidth <= 350) {
      window.open(`/c/${competition.slug}/terms-and-conditions`, "_blank")
    } else {
      showTerms = true
    }
  }

  function closeTermsDialog() {
    if (window.innerWidth <= 350) {
      window.open(`/c/${competition.slug}/terms-and-conditions`, "_blank")
    } else {
      showTerms = false
    }
  }

  function toggleReferrals() {
    showReferrals = !showReferrals
  }

  onMount(async () => {
    // Update stats store with entries count
    statsStoreActions.setStats({
      isLoading: false,
      yourEntries: 0,
      totalEntries: competition?.totalActionsCount || 0,
      isEnded: isCompetitionEnded(competition.endDate),
      timeLeft: isCompetitionEnded(competition.endDate)
        ? "Ended"
        : getTimeLeft(competition.endDate),
    })

    await userStoreActions.loadUser()
    const userData = $userStore.user

    if (userData) {
      const response = await fetch(`/api/user/get-entries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          slug: competition.slug,
          userId: userData.email,
          isPublic: true,
        }),
      })
      if (!response.ok) throw new Error("Failed to fetch entries")
      const data = await response.json()
      // Check for actions needing verification first
      const actionsNeedingVerification = competition?.competition_actions
        ?.filter((a) => a.is_active)
        ?.filter((action) => {
          const completedAction = data.completedActions.find(
            (a) => a.action_id === action.id,
          )
          if (!completedAction) return false
          if (!action.actions?.verification?.length) return false

          const verification = completedAction?.verification_data
          return (
            !verification ||
            !(
              verification?.value ||
              verification?.Url ||
              verification?.Username
            )
          )
        })

      if (actionsNeedingVerification && actionsNeedingVerification.length > 0) {
        const ids = actionsNeedingVerification.map((action) => action.id)
        userStoreActions.setActionsNeedingVerification(ids)
      }

      userStoreActions.setUser({
        ...$userStore.user,
        completedActionIds: data.completedActionIds,
        completedActions: data.completedActions,
      })

      statsStoreActions.setStats({
        yourEntries: data.totalEntries,
        referralCount: data.referralCount,
        referrals: data.referrals,
      })
    }

    isLoading = false

    // Don't show badge when embedded in Promo Amp
    if (embed) {
      try {
        const parentUrl = await getParentUrl()
        if (
          parentUrl.includes("promoamp.com") ||
          parentUrl.includes("localhost")
        ) {
          showCreateButton = false
        }
      } catch (e) {
        console.error("Error getting parent URL:", e)
      }
    }
  })
</script>

<div
  class="flex flex-col justify-center items-center rounded-[10px]"
  class:sm:pt-16={!embed}
  class:md:pt-4={layout === "landscape"}
>
  <BackgroundGradient
    className="rounded-3xl px-0 bg-white dark:bg-zinc-900"
    {backgroundColor}
  >
    <div
      class="mx-auto bg-white overflow-hidden shadow-lg sm:rounded-[6px] sm:shadow-xl w-full"
      class:max-w-xl={layout === "portrait"}
      class:md:w-[520px]={layout === "portrait"}
      class:md:w-[1020px]={layout === "landscape"}
      class:md:max-h-[90vh]={layout === "landscape"}
    >
      <ImageSection {competition} {layout} />

      <div
        class="p-4"
        style="background-color: {competition?.widgetCustomizations
          ?.primaryBackgroundColor}; direction: {textDirection};"
      >
        <HeaderSection {competition} />
        <StatsSection
          {competition}
          {isLoading}
          user={$userStore.user}
          {showReferrals}
        />
        <ActionSection
          {competition}
          {isCountryAllowed}
          {embed}
          {openTermsDialog}
          {isLoading}
          {toggleReferrals}
          {showReferrals}
        />
        <div class="text-center text-sm mt-8">
          <a
            href="#terms"
            class="text-gray-500 hover:underline"
            style="color: {competition?.widgetCustomizations
              ?.secondaryTextColor};"
            onclick={preventDefault(openTermsDialog)}
          >
            {t("widget_terms_and_conditions")}
          </a>
        </div>
      </div>
    </div>
  </BackgroundGradient>

  {#if showCreateButton}
    <div class="flex flex-col items-center justify-center mt-10" id="pa-cta">
      <a href={affiliateUrl} target="_blank" rel="noopener noreferrer">
        <Button
          variant="outline"
          size="lg"
          class="shadow-lg rounded-full px-6 py-5 mb-4"
        >
          <img
            src="https://www.promoamp.com/p-logo.svg"
            alt="Promo Amp Logo"
            class="w-5 h-5 mr-2"
          />
          {t("widget_launch_giveaway")}
        </Button>
      </a>
      <div class="text-sm text-gray-500 mt-2">
        {t("widget_powered_by")}
      </div>
    </div>
  {/if}
</div>

{#if showTerms}
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
  >
    <div
      class="tc-modal-dialog bg-white dark:bg-zinc-900 rounded-lg w-[90%] max-w-[510px] max-h-[90vh] relative"
    >
      <div class="p-4 border-b">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-semibold">
            {t("widget_terms_and_conditions")}
          </h2>
          <button
            class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            onclick={closeTermsDialog}
            aria-label="Close terms and conditions"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="p-4 px-10 overflow-y-auto h-[460px]">
        <div class="terms-content">
          <!-- svelte-ignore a11y-missing-attribute security-anchor-rel-noreferrer -->
          {@html generateTerms(competition)}
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  @media (max-width: 350px) {
    :global(body) {
      zoom: 0.7;
      font-size: 1rem;
      line-height: 1.2;
    }
    :global(p) {
      font-size: 0.8rem;
    }
    :global(h1.text-2xl) {
      font-size: 1.2rem;
      line-height: 1.2;
    }
    :global(.text-sm) {
      font-size: 0.7rem;
    }
    :global(.pr-2.sm:pl-0.leading-6.text-left) {
      font-size: 12px;
    }
    :global(.action-icon) {
      width: 40px;
      height: 40px;
    }
    :global(.leading-6) {
      line-height: 1rem;
    }
    :global(.gap-1) {
      gap: 0;
    }
    :global(.action-header-open) {
      padding: 0;
    }
    :global(span.flex.h-4.w-6.overflow-hidden.rounded-sm) {
      height: 19px;
      width: 28px;
    }
    :global(span.flag) {
      width: 35px !important;
      height: 23px !important;
    }
    :global(.tc-modal-dialog) {
      height: 520px !important;
      margin-top: -350px !important;
    }
    :global(.tc-modal-dialog h2) {
      font-size: 1rem;
      padding: 0;
    }
  }
</style>
